import React, { useEffect, useMemo, useState } from "react";
import { Menu } from "antd";
import * as Icons from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Module } from "../../types/Module";
import { buildMenuTree } from "./menuUtils";
import { customThemes } from "../../themeConfig";
import { logout } from "../../redux/slice/userSlice";
import { apiRequest } from "../../service/apiRequest";
import { useDispatch, useSelector } from "react-redux";

/* ===================== PROPS ===================== */
type Props = {
  modules: Module[];
  darkMode: boolean;
  siderRef: React.RefObject<HTMLDivElement | null>;
  collapsed: boolean;

  // ✅ Controlled props from Sidebar.tsx
  selectedKey: string;
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
};

/* ===================== ICON HELPER ===================== */
const getDynamicIcon = (iconName?: string) => {
  if (!iconName) return null;
  const IconComponent = (Icons as any)[iconName];
  return IconComponent ? <IconComponent /> : null;
};

/* ===================== MENU ITEMS BUILDER ===================== */
const buildMenuItems = (modules: Module[], onLogout?: () => void): any[] =>
  modules.map((mod) => {
    const normalizedName = (mod.module_name || "").toLowerCase().trim();
    const icon =
      getDynamicIcon(mod.module_icon) ||
      getDynamicIcon(mod.module_name) ||
      null;

    if (mod.children && mod.children.length > 0) {
      return {
        key: String(mod.module_id),
        icon,
        label: mod.module_name,
        children: buildMenuItems(mod.children, onLogout),
      };
    }

    if (normalizedName === "logout") {
      return {
        key: "logout",
        icon,
        label: "Logout",
        onClick: onLogout,
      };
    }

    return {
      key: String(mod.module_id),
      icon,
      label: <Link to={mod.module_url}>{mod.module_name}</Link>,
    };
  });

/* ===================== COMPONENT ===================== */
const SidebarMenu: React.FC<Props> = ({
  modules,
  darkMode,
  siderRef,
  collapsed,
  selectedKey,
  openKeys,
  onOpenChange,
}) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user?.user);
  const themeStyles = darkMode ? customThemes.dark : customThemes.light;

  const [siderWidth, setSiderWidth] = useState(240);

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
    try {
      if (user) await apiRequest({ method: "get", url: "admin-logout" });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  /* ================= MENU TREE ================= */
  const treeData = useMemo(() => buildMenuTree(modules), [modules]);
  const topMenuItems = buildMenuItems(treeData.filter(m => m.module_name?.toLowerCase() !== "logout"), handleLogout);
  const footerMenuItems = buildMenuItems(treeData.filter(m => m.module_name?.toLowerCase() === "logout"), handleLogout);

  /* ================= OBSERVE SIDER WIDTH ================= */
  useEffect(() => {
    if (!siderRef?.current) return;
    const observer = new ResizeObserver(() => {
      if (siderRef.current) setSiderWidth(siderRef.current.offsetWidth);
    });
    observer.observe(siderRef.current);
    return () => observer.disconnect();
  }, [siderRef]);

  /* ================= CUSTOM STYLES ================= */
  useEffect(() => {
    const styleId = "custom-theme-sidebar-style";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    const selectedBg = themeStyles.headerBg;
    const hoverBg = `${themeStyles.siderBg}cc`;
    const dropdownBg = themeStyles.siderBg;
    const textColor = themeStyles.textColor || "#fff";

    styleTag.innerHTML = `
      .ant-menu, .ant-menu-inline { background-color: ${dropdownBg} !important; }
      .ant-menu-item, .ant-menu-submenu-title { 
        color: ${textColor} !important; 
        transition: background-color 0.25s cubic-bezier(0.16, 1, 0.3, 1), color 0.25s ease !important;
      }
      .ant-menu-item-selected { background-color: ${selectedBg} !important; color: ${textColor} !important; }
      .ant-menu-item:hover, .ant-menu-submenu-title:hover { background-color: ${hoverBg} !important; }
    `;
  }, [themeStyles]);

  /* ================= RENDER ================= */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "89vh",
        backgroundColor: themeStyles.siderBg,
        justifyContent: "space-between",
      }}
    >
      {/* TOP MENU */}
      <Menu
        theme={darkMode ? "dark" : "light"}
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        items={topMenuItems}
        style={{
          flex: 1,
          backgroundColor: themeStyles.siderBg,
          border: "none",
        }}
      />

      {/* FOOTER MENU (LOGOUT) */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          width: siderWidth,
          backgroundColor: themeStyles.siderBg,
          zIndex: 100,
        }}
      >
        <Menu
          theme={darkMode ? "dark" : "light"}
          mode="inline"
          inlineCollapsed={collapsed}
          items={footerMenuItems}
          style={{
            backgroundColor: themeStyles.siderBg,
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        />
      </div>
    </div>
  );
};

export default SidebarMenu;